import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { fileApi } from "~/api";
import type { Item } from "~/types/api";

const getTaskItemQueryKey = (taskId: number, itemId: number) =>
  fileApi.queryOptions("get", "/api/task/{taskId}/item/{itemId}", {
    params: { path: { taskId, itemId } },
  }).queryKey;

const fetchTaskItem = async (taskId: number, itemId: number): Promise<Item> => {
  const response = await fileApi.client.GET("/api/task/{taskId}/item/{itemId}", {
    params: { path: { taskId, itemId } },
  });

  if (response.error) {
    throw new Error(`Failed to fetch task item ${itemId}: ${response.error.toString()}`);
  }

  if (!response.data) {
    throw new Error(`Item ${itemId} not found`);
  }

  return response.data as Item;
};

export const useTaskItem = (taskId: number, itemId: number, enabled = true) => {
  const { data, isLoading, error, refetch } = useQuery<Item>({
    queryKey: getTaskItemQueryKey(taskId, itemId),
    queryFn: () => fetchTaskItem(taskId, itemId),
    meta: { errorTitle: "Failed to load item details" },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    item: data,
    isLoading,
    error,
    refetch,
  };
};

export const usePrefetchTaskItemByID = () => {
  const queryClient = useQueryClient();

  const prefetchItem = async (taskId: number, itemId: number) => {
    await queryClient.prefetchQuery({
      queryKey: getTaskItemQueryKey(taskId, itemId),
      queryFn: () => fetchTaskItem(taskId, itemId),
      staleTime: 5 * 60 * 1000,
    });
  };

  return { prefetchItem };
};

export const useFetchItems = () => {
  const queryClient = useQueryClient();

  const fetchSingleItem = useCallback(
    async (taskId: number, itemId: number): Promise<Item> => {
      return await queryClient.fetchQuery({
        queryKey: getTaskItemQueryKey(taskId, itemId),
        queryFn: () => fetchTaskItem(taskId, itemId),
        meta: { errorTitle: `Failed to load item ${itemId}` },
        staleTime: 0,
      });
    },
    [queryClient]
  );

  const fetchMultipleItems = useCallback(
    async (taskId: number, itemIds: number[]): Promise<Item[]> => {
      try {
        const itemsPromises = itemIds.map((itemId) =>
          queryClient.fetchQuery({
            queryKey: getTaskItemQueryKey(taskId, itemId),
            queryFn: () => fetchTaskItem(taskId, itemId),
            meta: { errorTitle: `Failed to load item ${itemId}` },
            staleTime: 0,
          })
        );

        const items = await Promise.all(itemsPromises);
        return items;
      } catch (error) {
        const errorMessage = (error as Error).message;
        console.error("Failed to fetch multiple items:", errorMessage);
        throw error;
      }
    },
    [queryClient]
  );

  const invalidateItems = useCallback(
    (taskId: number, itemIds: number[]) => {
      itemIds.forEach((itemId) => {
        queryClient.invalidateQueries({
          queryKey: getTaskItemQueryKey(taskId, itemId),
        });
      });
    },
    [queryClient]
  );

  return {
    fetchSingleItem,
    fetchMultipleItems,
    invalidateItems,
  };
};

export { getTaskItemQueryKey, fetchTaskItem };
